import { interpolate, formatRgb, parse } from 'culori';

export type PaletteStop = { height: number; color: string }; // color is hex '#rrggbb'
export type Palette = { id: string; name: string; stops: PaletteStop[] };

export const DEFAULT_PALETTE_ID = 'blue-red';

// Compile-time palette definitions. OKLCH is used for interpolation (perceptually uniform).
export const PALETTES: Record<string, Palette> = {
  'blue-red': {
    id: 'blue-red',
    name: 'Blue → Red (default)',
    stops: [
      { height: 0.00, color: '#1e3a8a' }, // deep blue
      { height: 0.25, color: '#4338ca' }, // blue-violet
      { height: 0.50, color: '#7c3aed' }, // violet
      { height: 0.75, color: '#e11d48' }, // pink-red
      { height: 1.00, color: '#dc2626' }, // MAX RED — highest elevation (spec §4.2)
    ],
  },
  'terrain-classic': {
    id: 'terrain-classic', name: 'Terrain',
    stops: [
      { height: 0.00, color: '#0c4a6e' },
      { height: 0.40, color: '#1e90ff' },
      { height: 0.45, color: '#f4e3a1' },
      { height: 0.60, color: '#4d7c0f' },
      { height: 0.80, color: '#78716c' },
      { height: 1.00, color: '#ffffff' },
    ],
  },
  grayscale: {
    id: 'grayscale', name: 'Grayscale',
    stops: [{ height: 0, color: '#000000' }, { height: 1, color: '#ffffff' }],
  },
  inferno: {
    id: 'inferno', name: 'Inferno',
    stops: [
      { height: 0.00, color: '#000004' },
      { height: 0.25, color: '#420a68' },
      { height: 0.50, color: '#932667' },
      { height: 0.75, color: '#dd513a' },
      { height: 1.00, color: '#fcffa4' },
    ],
  },
  viridis: {
    id: 'viridis', name: 'Viridis',
    stops: [
      { height: 0.00, color: '#440154' },
      { height: 0.25, color: '#3b528b' },
      { height: 0.50, color: '#21918c' },
      { height: 0.75, color: '#5ec962' },
      { height: 1.00, color: '#fde725' },
    ],
  },
  'ocean-land': {
    id: 'ocean-land', name: 'Ocean & Land',
    stops: [
      { height: 0.00, color: '#082f49' },
      { height: 0.40, color: '#38bdf8' },
      { height: 0.41, color: '#fef3c7' },
      { height: 0.60, color: '#65a30d' },
      { height: 1.00, color: '#fafafa' },
    ],
  },
};

export function getPalette(id: string): Palette | undefined {
  return PALETTES[id];
}

// Cached interpolators per palette id.
const interpCache = new Map<string, (t: number) => string>();
function getInterpolator(p: Palette): (t: number) => string {
  const cached = interpCache.get(p.id);
  if (cached !== undefined) return cached;

  // Culori builds a piecewise interpolator across colors in OKLCH.
  const colors = p.stops.map((s) => s.color);
  const fn = interpolate(colors, 'oklch');

  // Remap to the stop heights.
  const interp = (t: number): string => {
    const clamped = t <= 0 ? 0 : t >= 1 ? 1 : t;
    // Find segment.
    for (let i = 1; i < p.stops.length; i++) {
      const a = p.stops[i - 1]!;
      const b = p.stops[i]!;
      if (clamped <= b.height) {
        const local = (clamped - a.height) / (b.height - a.height || 1);
        const u = (i - 1 + local) / (p.stops.length - 1);
        return formatRgb(fn(u));
      }
    }
    return colors[colors.length - 1]!;
  };

  interpCache.set(p.id, interp);
  return interp;
}

export function sampleGradient(p: Palette, height: number): [number, number, number] {
  const rgbString = getInterpolator(p)(height); // e.g. "rgb(220, 38, 38)"
  const parsed = parse(rgbString);
  if (parsed === undefined || parsed.mode !== 'rgb') return [0, 0, 0];
  return [
    Math.round(parsed.r * 255),
    Math.round(parsed.g * 255),
    Math.round(parsed.b * 255),
  ];
}
